import { UserRole } from '@/__generated__/types';
import { useTranslation } from 'react-i18next';
import SvgIconStyle from '../../components/SvgIconStyle';
import { PATH_DASHBOARD } from '../../routes/paths';

// ----------------------------------------------------------------------

export interface SidebarSection {
    title: string;
    path?: string;
    icon?: JSX.Element;
    includeRoles?: UserRole[];
    excludeRoles?: UserRole[];
    items?: SidebarSection[];
    isLocked?: boolean;
}

const getIcon = (name: string): JSX.Element => (
    <SvgIconStyle src={`/static/icons/navbar/${name}.svg`} sx={{ width: '100%', height: '100%' }} />
);

const ICONS = {
    blog: getIcon('ic_blog'),
    cart: getIcon('ic_cart'),
    chat: getIcon('ic_chat'),
    mail: getIcon('ic_mail'),
    user: getIcon('ic_user'),
    kanban: getIcon('ic_kanban'),
    banking: getIcon('ic_banking'),
    calendar: getIcon('ic_calendar'),
    ecommerce: getIcon('ic_ecommerce'),
    analytics: getIcon('ic_analytics'),
    dashboard: getIcon('ic_dashboard'),
    promotion: getIcon('ic_promotion'),
    loyalty: getIcon('ic_loyalty'),
    merchant: getIcon('ic_merchant'),
    menu: getIcon('ic_menu'),
    order: getIcon('ic_order'),
    transactions: getIcon('ic_transaction'),
    reports: getIcon('ic_reports'),
    settings: getIcon('ic_settings'),
    giftCard: getIcon('card_giftcard'),
    question: getIcon('ic_question'),
    health: getIcon('ix_health'),
    controlPanel: getIcon('control_panel'),
    userFill: getIcon('user_fill'),
    shop: getIcon('shop'),
    booking: getIcon('booking'),
    fee: getIcon('ic_outline_fee'),
    people_group: getIcon('people_group'),
    alert: getIcon('alert'),
    ticket: getIcon('ic_question')
};

export const TICKET_SIDEBAR_ROLES = [
    UserRole.Admin,
    UserRole.MerchantAdmin,
    UserRole.MerchantGroupAdmin,
    UserRole.BookingMerchantAdmin,
    UserRole.BookingManager,
    UserRole.MerchantManager
];

const useSidebarConfig = (userRoles: UserRole[]): SidebarSection[] => {
    const { t } = useTranslation();
    // const merchant = useSelector((state: RootState) => state.auth?.merchant) || null;

    // Home
    const home: SidebarSection = {
        title: t('HOME'),
        includeRoles: [
            UserRole.Admin,
            UserRole.MarketingAdmin,
            UserRole.GiftCardAdmin,
            UserRole.BookingAdmin,
            UserRole.MerchantAdmin,
            UserRole.MerchantGroupAdmin,
            UserRole.BookingMerchantAdmin,
            UserRole.MenuAdmin,
            UserRole.MembershipAdmin,
            UserRole.MerchantManager
        ],
        items: [
            {
                title: t('Orderbuddy Dashboard'),
                path: PATH_DASHBOARD.general.generalApp,
                icon: ICONS.controlPanel,
                includeRoles: [
                    UserRole.Admin,
                    UserRole.MarketingAdmin,
                    UserRole.GiftCardAdmin,
                    UserRole.BookingAdmin,
                    UserRole.MerchantAdmin,
                    UserRole.MerchantGroupAdmin,
                    UserRole.MenuAdmin,
                    UserRole.MembershipAdmin,
                    UserRole.BookingManager,
                    UserRole.MerchantManager
                ]
            },
            {
                title: t('Merchant Dashboard'),
                path: PATH_DASHBOARD.general.generalApp,
                icon: ICONS.controlPanel
            }
        ]
    };

    const admin: SidebarSection = {
        title: t('ADMIN'),
        includeRoles: [UserRole.Admin, UserRole.BookingAdmin, UserRole.MarketingAdmin, UserRole.GiftCardAdmin],
        items: [
            {
                title: t('User Roles'),
                path: PATH_DASHBOARD.user.list,
                icon: ICONS.userFill
            },
            {
                title: t('Merchants'),
                path: PATH_DASHBOARD.merchant.list,
                icon: ICONS.shop
            },
            {
                title: t('Booking Report'),
                icon: ICONS.analytics,
                includeRoles: [UserRole.Admin, UserRole.BookingAdmin],
                items: [
                    {
                        title: t('Platform Report'),
                        path: PATH_DASHBOARD.booking.platformReport,
                        includeRoles: [UserRole.Admin]
                    },
                    {
                        title: t('SMS Report'),
                        path: PATH_DASHBOARD.booking.smsReport,
                        includeRoles: [UserRole.Admin, UserRole.BookingAdmin]
                    }
                ]
            },
            {
                title: t('Forms'),
                path: PATH_DASHBOARD.forms.root,
                icon: ICONS.analytics,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin],
                items: [
                    { title: t('POS'), path: PATH_DASHBOARD.forms.pos },
                    { title: t('Booking'), path: PATH_DASHBOARD.forms.booking }
                ]
            },
            {
                title: t('OB Dollar'),
                path: PATH_DASHBOARD.giftCard.root,
                icon: ICONS.giftCard,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin],
                items: [
                    {
                        title: t('Account'),
                        includeRoles: [UserRole.Admin],
                        path: PATH_DASHBOARD.giftCard.accounts
                    },
                    { title: t('Balance'), path: PATH_DASHBOARD.giftCard.balance },
                    { title: t('History'), path: PATH_DASHBOARD.giftCard.history }
                ]
            },
            {
                title: t('Announcement Broadcast'),
                path: PATH_DASHBOARD.announcementBroadcast.list,
                icon: ICONS.alert,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin, UserRole.BookingAdmin],
                items: [
                    { title: t('List'), path: PATH_DASHBOARD.announcementBroadcast.list },
                    { title: t('Create'), path: PATH_DASHBOARD.announcementBroadcast.create }
                ]
            },
            {
                title: t('OB APP Settings'),
                icon: ICONS.settings,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin],
                items: [
                    {
                        title: t('App Sections'),
                        path: PATH_DASHBOARD.appSections.list
                    },
                    {
                        title: t('Popular Restaurant Categories'),
                        path: PATH_DASHBOARD.popularRestaurantCategories.list
                    },
                    {
                        title: t('FAQ'),
                        path: PATH_DASHBOARD.FAQ.sections
                    }
                ]
            },
            {
                title: t('Booking Subscription'),
                icon: ICONS.loyalty,
                includeRoles: [UserRole.Admin, UserRole.BookingAdmin],
                items: [
                    {
                        title: t('Packages'),
                        path: PATH_DASHBOARD.bookingSubscription.packages,
                        items: [
                            { title: t('List'), path: PATH_DASHBOARD.bookingSubscription.packages },
                            { title: t('Create'), path: PATH_DASHBOARD.bookingSubscription.createPackage }
                        ]
                    },
                    {
                        title: t('Merchant Subscriptions'),
                        path: PATH_DASHBOARD.bookingSubscription.merchantSubscriptions
                    }
                ]
            }
        ]
    };

    const myMerchant: SidebarSection = {
        title: t('MY MERCHANT'),
        includeRoles: [
            UserRole.Admin,
            UserRole.MarketingAdmin,
            UserRole.GiftCardAdmin,
            UserRole.BookingAdmin,
            UserRole.MerchantAdmin,
            UserRole.MerchantGroupAdmin,
            UserRole.BookingMerchantAdmin,
            UserRole.MenuAdmin,
            UserRole.MembershipAdmin,
            UserRole.BookingManager,
            UserRole.MerchantManager
        ],
        items: [
            {
                title: t('Merchant'),
                icon: ICONS.shop,
                includeRoles: [
                    UserRole.Admin,
                    UserRole.MarketingAdmin,
                    UserRole.GiftCardAdmin,
                    UserRole.BookingAdmin,
                    UserRole.MerchantAdmin,
                    UserRole.MerchantGroupAdmin,
                    UserRole.BookingMerchantAdmin,
                    UserRole.MenuAdmin
                ],
                items: [
                    {
                        title: t('Update Merchant'),
                        path: PATH_DASHBOARD.merchant.updateMerchant
                    },
                    {
                        title: t('Opening Hours'),
                        path: PATH_DASHBOARD.merchant.updateOpeningHours
                    },
                    {
                        title: t('Managers'),
                        path: PATH_DASHBOARD.managers.list,
                        includeRoles: [
                            UserRole.Admin,
                            UserRole.BookingAdmin,
                            UserRole.MarketingAdmin,
                            UserRole.MerchantAdmin,
                            UserRole.MerchantGroupAdmin,
                            UserRole.BookingMerchantAdmin
                        ]
                    }
                ]
            },
            {
                title: t('Ticket'),
                path: PATH_DASHBOARD.ticket.view,
                icon: ICONS.ticket,
                includeRoles: TICKET_SIDEBAR_ROLES,
                items: [
                    { title: t('Create'), path: PATH_DASHBOARD.ticket.create },
                    { title: t('View'), path: PATH_DASHBOARD.ticket.view }
                ]
            },
            {
                title: t('Promotion'),
                path: PATH_DASHBOARD.promotion.root,
                icon: ICONS.transactions,
                includeRoles: [
                    UserRole.Admin,
                    UserRole.MarketingAdmin,
                    UserRole.MerchantAdmin,
                    UserRole.MerchantGroupAdmin,
                    UserRole.MerchantManager
                ],
                items: [
                    { title: t('List'), path: PATH_DASHBOARD.promotion.list },
                    { title: t('Create'), path: PATH_DASHBOARD.promotion.newPromotion },
                    { title: t('Send'), path: PATH_DASHBOARD.promotion.sendPromotion },
                    { title: t('Send History'), path: PATH_DASHBOARD.promotion.sendHistory },
                    { title: t('History'), path: PATH_DASHBOARD.promotion.history },
                    { title: t('App QR Code'), path: PATH_DASHBOARD.promotion.appQRCode }
                ]
            },
            {
                title: t('Loyalty'),
                path: PATH_DASHBOARD.loyalty.root,
                icon: ICONS.loyalty,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin, UserRole.MerchantAdmin, UserRole.MerchantGroupAdmin],

                items: [
                    { title: t('Members'), path: PATH_DASHBOARD.loyalty.member },
                    { title: t('Member Detail'), path: PATH_DASHBOARD.loyalty.memberDetail }
                ]
            },
            {
                title: t('Membership'),
                path: PATH_DASHBOARD.membership.root,
                icon: ICONS.people_group,
                includeRoles: [
                    UserRole.Admin,
                    UserRole.MarketingAdmin,
                    UserRole.MerchantAdmin,
                    UserRole.MerchantGroupAdmin,
                    UserRole.MembershipAdmin
                ],

                items: [
                    { title: t('Members'), path: PATH_DASHBOARD.membership.members },
                    { title: t('Membership Plans'), path: PATH_DASHBOARD.membership.plans }
                ]
            },
            {
                title: t('Merchant Gift Card'),
                path: PATH_DASHBOARD.merchantGiftCard.root,
                icon: ICONS.giftCard,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin, UserRole.MerchantAdmin, UserRole.MerchantGroupAdmin],

                items: [
                    { title: t('Wallets'), path: PATH_DASHBOARD.merchantGiftCard.wallets },
                    { title: t('Gift Cards'), path: PATH_DASHBOARD.merchantGiftCard.member },
                    { title: t('Create'), path: PATH_DASHBOARD.merchantGiftCard.createGiftCard }
                ]
            },
            {
                title: t('Bookings'),
                icon: ICONS.booking,
                items: [
                    {
                        title: t('Booking List'),
                        path: PATH_DASHBOARD.booking.list()
                    },
                    {
                        title: t('Calendar'),
                        path: PATH_DASHBOARD.booking.calendar
                    },
                    {
                        title: t('Reports'),
                        path: PATH_DASHBOARD.booking.report
                    },
                    {
                        title: t('Clients'),
                        path: PATH_DASHBOARD.booking.clients
                    },
                    {
                        title: t('Create'),
                        path: PATH_DASHBOARD.booking.createBooking
                    },
                    {
                        title: t('Booking Link'),
                        path: PATH_DASHBOARD.booking.link
                    }
                ]
            },
            {
                title: t('Booking Setup'),
                icon: ICONS.booking,
                includeRoles: [
                    UserRole.Admin,
                    UserRole.MarketingAdmin,
                    UserRole.BookingAdmin,
                    UserRole.MerchantAdmin,
                    UserRole.MerchantGroupAdmin,
                    UserRole.BookingMerchantAdmin
                ],
                items: [
                    {
                        title: t('Settings'),
                        path: PATH_DASHBOARD.booking.settings
                    },
                    {
                        title: t('Booking Hours'),
                        path: PATH_DASHBOARD.booking.updateBookingHours
                    },
                    {
                        title: t('Tables'),
                        path: PATH_DASHBOARD.booking.tables
                    },
                    {
                        title: t('Floormap'),
                        path: PATH_DASHBOARD.booking.floormap
                    },
                    {
                        title: t('Booking Terms'),
                        path: PATH_DASHBOARD.booking.message
                    },
                    {
                        title: t('Google Reservations'),
                        path: PATH_DASHBOARD.booking.google
                    }
                ]
            },
            {
                title: t('My Subscription'),
                path: PATH_DASHBOARD.bookingSubscription.mySubscription,
                icon: ICONS.loyalty,
                includeRoles: [
                    UserRole.Admin,
                    UserRole.BookingAdmin,
                    UserRole.MerchantAdmin,
                    UserRole.MerchantGroupAdmin,
                    UserRole.BookingMerchantAdmin,
                    UserRole.BookingManager,
                    UserRole.MerchantManager
                ]
            }
        ]
    };

    const portal: SidebarSection = {
        title: t('PORTAL'),
        includeRoles: [],
        items: [
            {
                title: t('Settings'),
                path: PATH_DASHBOARD.user.account,
                icon: ICONS.settings
            },
            {
                title: t('Short URL'),
                path: PATH_DASHBOARD.system.shortUrl,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin, UserRole.BookingAdmin, UserRole.GiftCardAdmin],
                icon: ICONS.mail
            },
            {
                title: t('API Status'),
                path: PATH_DASHBOARD.system.apiHealth,
                includeRoles: [UserRole.Admin, UserRole.MarketingAdmin, UserRole.BookingAdmin, UserRole.GiftCardAdmin],
                icon: ICONS.health
            }
        ]
    };

    // create a empty SidebarConfig
    const sidebarConfig: SidebarSection[] = [];
    // add the home, body and foot to the sidebarConfig
    sidebarConfig.push(home);
    sidebarConfig.push(admin);
    sidebarConfig.push(myMerchant);

    sidebarConfig.push(portal);

    const checkUserRole = (section: SidebarSection): boolean => {
        if (userRoles.includes(UserRole.Admin)) {
            return true;
        }

        if (section.includeRoles && section.includeRoles.length > 0) {
            return section.includeRoles.some((role) => userRoles.includes(role));
        }

        return true;
    };

    const checkSidebarSectionList = (sectionList: SidebarSection[]): SidebarSection[] => {
        const newSectionList: SidebarSection[] = [];
        sectionList.forEach((section) => {
            if (checkUserRole(section)) {
                if (section.items) {
                    const newItems = checkSidebarSectionList(section.items);
                    if (newItems.length > 0) {
                        newSectionList.push({ ...section, items: newItems });
                    }
                } else {
                    newSectionList.push(section);
                }
            }
        });
        return newSectionList;
    };

    return checkSidebarSectionList(sidebarConfig);
};
export default useSidebarConfig;
